import React, { useState, useEffect, useContext, useMemo } from 'react'
import { EmptyPageCenter, Page } from '../Elements/PageMargins'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, useLocation } from 'react-router'
import ProgramFormCard from '../Card/ProgramFormCard'
import { Program, ProgramType } from '../../swagger'
import TitleContext from '../../TitleContext'
import DynamicBreadcrumbs from '../Elements/DynamicBreadcrumbs'
import { Ability } from '@casl/ability'
import { programs } from '../../api/swagger'
import EmptyProgramDetails from './EmptyProgramDetails'
import { useAuth } from '../Routes/AuthProvider'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import Tab from '@mui/material/Tab'
import { CanAccess } from '../Elements/Access'
import EnrollmentsTable from '../Families/EnrollmentsTable'
import { useEnrollmentOptions } from '../../hooks/useEnrollmentOptions'
import DropdownSelectOptions from '../Interfaces/DropdownSelectOptions'
import CircularProgress from '@mui/material/CircularProgress'
import useLoadingContext from '../../hooks/useLoadingContext'
import { Box } from '@mui/material'
import { useProgramDetailsContext } from '../Context/ProgramDetailsContext'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import { ACTING_AS_PARENT } from '../../utils/constants'
import { useUnmountEffect } from '../../hooks/useUnmountEffect'
import { ProgramFormCardVariants } from '../Card/ProgramFormCard/types'
import { useAppSnackbar } from '../../hooks/useAppSnackbar'

export enum ProgramDetailsTab {
  programDetails = '1',
  enrollments = '2',
}

const ProgramDetails: React.FC = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { permissionAbility, userDetails } = useAuth()
  const title = t('Programs.ProgramDetails.Header', 'Programs')
  const { handleError } = useAppSnackbar()
  const location: {
    state: {
      academicYear: string
      initiallySelectedTab: string
      tabToChange: string
    }
    pathname: string
  } = useLocation()

  const {
    ProgramDetails: {
      fetchProgram: fetchedProgramDetailsLoadingId,
      fetchEnrollments: fetchEnrollmentsLoadingId,
    },
  } = useLoadingIds()
  const { useTitleEffect } = useContext(TitleContext)

  const selectTabLocation = () => {
    switch (true) {
      case /\/enrollments/.test(location.pathname):
        return ProgramDetailsTab.enrollments
      default:
        return ProgramDetailsTab.programDetails
    }
  }

  const selectedTab = selectTabLocation()
  useTitleEffect(title)

  const {
    enrollmentFilters,
    enrollments,
    enrollmentsPagination,
    searchEnrollments,

    fetchEnrollments,
    handleEnrollmentsPageChange,
    handleEnrollmentsPageSizeChange,
    updateEnrollmentFilters,
    updateSearchEnrollments,
    resetContextToDefaults,
  } = useProgramDetailsContext()

  /**
   * null: the program has not been fetched yet
   * undefined: the program was fetched but not found
   */
  const [programDetails, setProgramDetails] = useState<
    Program | null | undefined
  >(null)

  const { programKey, communityKey } = useParams<{
    programKey: string
    communityKey: string
  }>()
  const programId = parseInt(`${programKey}`)

  const programAbility = useMemo(
    () =>
      new Ability(
        programDetails?.meta?.permissions?.map(
          ({ resourceCode: subject, actionCode: action }) => ({
            subject,
            action,
          })
        )
      ),
    [programDetails]
  )

  const canViewEnrollments = permissionAbility.can('view', 'Enrollment')
  /**
   * Parents should not call the fetchEnrollments endpoint (WF-1540900)
   * unless they have been granted permission to view enrollments.
   */
  const canFetchEnrollments =
    canViewEnrollments || userDetails.actingAs !== ACTING_AS_PARENT

  const baseUrlProgramsDetails = `/communities/community-details/${communityKey}/programs/program-details/${programKey}`

  const setSelectedTab = React.useCallback(
    (newValue: string) => {
      if (newValue === selectedTab) return
      switch (newValue) {
        case ProgramDetailsTab.enrollments:
          navigate(`enrollments`)
          break
        default:
          navigate(`${baseUrlProgramsDetails}`)
          return
      }
    },
    [selectedTab, navigate, baseUrlProgramsDetails]
  )

  useUnmountEffect(resetContextToDefaults)

  /** Other Objects */
  const defaultErrorMessage = t(
    'ProgramDetails.Error.FetchProgramDetails',
    'Error occurred while retrieving program details'
  )

  const programTitle = t(
    'ProgramDetails.TabTitle.ProgramTitle',
    'Program Details'
  )

  const enrollmentsTitle = t(
    'ProgramDetails.TabTitle.EnrollmentsTitle',
    'Enrollments'
  )

  const fetchProgramDetails = async () => {
    try {
      const fetchedProgramDetails = await programs.fetchProgram({ programId })
      setProgramDetails(fetchedProgramDetails ?? undefined)
    } catch (e) {
      await handleError(e, defaultErrorMessage)
    }
  }

  const { triggerFetch: triggerFetchProgramDetails, loadingIds } =
    useLoadingContext({
      loadingId: fetchedProgramDetailsLoadingId,
      asyncFunction: fetchProgramDetails,
    })

  useEffect(() => {
    triggerFetchProgramDetails()
  }, [programId, triggerFetchProgramDetails])

  /**
   * Fetch Enrollments
   */

  const fetchProgramEnrollments = async () => {
    try {
      await fetchEnrollments(programId)
    } catch (e) {
      await handleError(e, defaultErrorMessage)
    }
  }

  const { triggerFetch: triggerFetchProgramEnrollments } = useLoadingContext({
    loadingId: fetchEnrollmentsLoadingId,
    asyncFunction: fetchProgramEnrollments,
  })

  useEffect(() => {
    if (canFetchEnrollments) {
      triggerFetchProgramEnrollments()
    }
  }, [
    programId,
    searchEnrollments,
    enrollmentFilters,
    enrollmentsPagination.page,
    enrollmentsPagination.pageSize,
    canFetchEnrollments,
    triggerFetchProgramEnrollments,
  ])

  const enrollmentOptions = useEnrollmentOptions({
    permissionAbility: permissionAbility,
    programKey: programId,
  })

  const onCommunitiesBreadCrumbClick = () => {
    navigate({ pathname: '/communities' })
  }

  //If no academic year from location, set academic year value from the program semesterOneStartDate
  const academicYear =
    location.state?.academicYear ??
    new Date(programDetails?.semesterOneStartDate as Date)
      .getUTCFullYear()
      .toString()

  const communityBreadcrumbLabel = `${programDetails?.communityName} (${academicYear})`

  const onCommunityBreadCrumbClick = () => {
    const selectedYearQueryString = !!academicYear
      ? new URLSearchParams({ year: academicYear }).toString()
      : ''

    navigate(
      {
        pathname: `/communities/community-details/${programDetails?.communityId}/overview`,
        search: selectedYearQueryString,
      },
      {
        state: {
          communityKey: programDetails?.communityId,
          communityName: programDetails?.communityName,
        },
      }
    )
  }

  const labelForBreadcrumb = () => {
    let label
    switch (programDetails?.type) {
      case ProgramType.Scribblers:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.ScribblersDetails',
          'Scribblers'
        )
        break
      case ProgramType.Foundations:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.FoundationsDetails',
          'Foundations'
        )
        break
      case ProgramType.Essentials:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.EssentialsDetails',
          'Essentials'
        )
        break
      case ProgramType.ChallengeA:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.ChallengeADetails',
          'Challenge A'
        )
        break
      case ProgramType.ChallengeB:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.ChallengeBDetails',
          'Challenge B'
        )
        break
      case ProgramType.ChallengeI:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.ChallengeIDetails',
          'Challenge I '
        )
        break
      case ProgramType.ChallengeIi:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.ChallengeIIDetails',
          'Challenge II'
        )
        break
      case ProgramType.ChallengeIii:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.ChallengeIIIDetails',
          'Challenge III'
        )
        break
      case ProgramType.ChallengeIv:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.ChallengeIVDetails',
          'Challenge IV'
        )
        break
      default:
        label = ''
    }
    return label
  }

  // 1) Not loaded yet ,initial state before fetch finishes
  if (programDetails === null) {
    return <EmptyProgramDetails isLoading />
  }
  // 2) Loaded but empty/not found
  if (programDetails === undefined) {
    return <EmptyProgramDetails isLoading={false} />
  }
  const Tabs = [
    <Tab
      key="1"
      label={programTitle}
      value={ProgramDetailsTab.programDetails}
    />,
    ...(permissionAbility.can('view', 'Enrollment')
      ? [
          <Tab
            key="2"
            label={enrollmentsTitle}
            value={ProgramDetailsTab.enrollments}
          />,
        ]
      : []),
  ]

  const programBreadCrumb = [
    {
      label: t('Programs.ProgramDetails.BreadCrumb', 'Communities'),
      onClick: onCommunitiesBreadCrumbClick,
    },
    {
      label: communityBreadcrumbLabel,
      onClick: onCommunityBreadCrumbClick,
    },
    {
      label: labelForBreadcrumb(),
    },
  ]

  return (
    <TabContext value={selectedTab}>
      <TabList
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
        textColor="primary"
        variant="scrollable"
        scrollButtons={true}
      >
        {Tabs}
      </TabList>
      <TabPanel value={ProgramDetailsTab.programDetails}>
        <Page withinTab>
          <DynamicBreadcrumbs breadcrumbs={programBreadCrumb} />
          <Box
            sx={{
              color: theme.palette.primary.main,
              maxWidth: 1200,
              [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                display: 'flex',
              },
            }}
          >
            <section aria-label="programFormCard">
              {loadingIds.has(fetchedProgramDetailsLoadingId) ? (
                <EmptyPageCenter adjustForMargins>
                  <CircularProgress />
                </EmptyPageCenter>
              ) : (
                <ProgramFormCard
                  variant={ProgramFormCardVariants.EditProgram}
                  totalEnrolledStudents={
                    enrollmentsPagination
                      ? enrollmentsPagination.totalCount
                      : enrollments.length
                  }
                  programDetails={programDetails}
                  handleSave={triggerFetchProgramDetails}
                  programAbility={programAbility}
                  hasEnrollment={enrollments.length > 0}
                  isProgramDetailsLoading
                />
              )}
            </section>
          </Box>
        </Page>
      </TabPanel>
      <TabPanel value={ProgramDetailsTab.enrollments}>
        <Page withinTab>
          <DynamicBreadcrumbs breadcrumbs={programBreadCrumb} />
          <CanAccess I="view" on="Enrollment">
            {loadingIds.has(fetchEnrollmentsLoadingId) ? (
              <EmptyPageCenter adjustForMargins>
                <CircularProgress />
              </EmptyPageCenter>
            ) : (
              <section aria-label="enrollments">
                <EnrollmentsTable
                  pagination={enrollmentsPagination}
                  handleChangePage={handleEnrollmentsPageChange}
                  handleChangePageSize={handleEnrollmentsPageSizeChange}
                  searchText={searchEnrollments}
                  setSearchState={updateSearchEnrollments}
                  filters={enrollmentFilters}
                  setFilters={updateEnrollmentFilters}
                  enrollments={enrollments}
                  enrollmentOptions={enrollmentOptions?.statuses ?? []}
                  refetchEnrollmentsData={triggerFetchProgramEnrollments}
                  tutors={
                    programDetails.tutors?.map((it) => {
                      return {
                        id: it.actorKey,
                        label: it.tutorName,
                      } as DropdownSelectOptions
                    }) ?? []
                  }
                  programInfo={{
                    programKey: programDetails.programsKey,
                    programType: programDetails.type,
                    status: programDetails.status,
                    firstSemesterStartDate: programDetails.semesterOneStartDate,
                  }}
                  academicYear={academicYear}
                  programAbility={programAbility}
                  communityId={programDetails.communityId}
                  programEmailDirector={programDetails.directorEmail}
                />
              </section>
            )}
          </CanAccess>
        </Page>
      </TabPanel>
    </TabContext>
  )
}

export default ProgramDetails
